import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./EAModal.scss";
import { IoMdClose } from "react-icons/io";
import { IoChevronBack } from "react-icons/io5";
import EAIconButton from "../Buttons/EAIconButton";
import EAButton from "../Buttons/EAButton";
import { useTranslation } from "react-i18next";
import EASkeletonLoader from "../EASkeletonLoader";
import useEAModalIframeScrolling from "./useEAModalIframeScrolling";
import useParentMessage from "../../../../utils/hooks/useParentMessage";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onAccept?: () => void;
  hideButtons?: boolean;
  cancelButtonLabel?: string;
  acceptButtonLabel?: string;
  hideAcceptButton?: boolean;
  loading?: boolean;
  isCloseButton?: boolean;
  desktopWidth?: string;
  onBack?: () => void;
  /** Marginesy od krawędzi viewportu (np. dla fixed nav). Na mobile domyślnie 80px góra/dół. */
  verticalMarginTop?: number;
  verticalMarginBottom?: number;
  disabledAccept?: boolean;
  /** Przypina footer z przyciskami na stałe do dołu modala — content scrolluje niezależnie. */
  stickyFooter?: boolean;
}

const EAModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  title,
  children,
  onAccept,
  hideButtons = false,
  cancelButtonLabel = "",
  acceptButtonLabel = "",
  hideAcceptButton = false,
  loading = false,
  isCloseButton = true,
  desktopWidth,
  onBack,
  verticalMarginTop,
  verticalMarginBottom,
  disabledAccept = false,
  stickyFooter = false,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const isDesktop = useDesktopSize();
  const safeArea =
    verticalMarginTop !== undefined || verticalMarginBottom !== undefined
      ? {
          verticalMarginTop: verticalMarginTop ?? (isDesktop ? 5 : 80),
          verticalMarginBottom: verticalMarginBottom ?? (isDesktop ? 5 : 80),
        }
      : isDesktop
        ? undefined
        : { verticalMarginTop: 80, verticalMarginBottom: 80 };
  const { modalTop, maxHeight } = useEAModalIframeScrolling(modalRef, safeArea);
  const { updateScrollPosition } = useParentMessage();

  // Blokujemy przewijanie tła
  useEffect(() => {
    if (isOpen) {
      // Daj czas na wyrenderowanie DOM przed wysłaniem request
      requestAnimationFrame(() => {
        updateScrollPosition();
      });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
    //eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="eamodal-overlay"
      onClick={(e) => {
        if ((e.target as HTMLElement).classList.contains("eamodal-overlay")) {
          onClose();
        }
      }}
    >
      <div
        className={`eamodal-container${stickyFooter && !hideButtons ? " eamodal-container--sticky-footer" : ""}`}
        ref={modalRef}
        style={{
          position: "absolute",
          top: modalTop,
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: maxHeight,
          ...(desktopWidth && isDesktop ? { width: desktopWidth } : {}),
        }}
      >
        <div className="eamodal-header">
          {onBack && <EAIconButton title="Cofnij" icon={<IoChevronBack />} onClick={onBack} variant="icon" />}
          <h5 style={onBack ? { marginLeft: "8px" } : {}}>{title}</h5>
          {isCloseButton && <EAIconButton title="Zamknij" icon={<IoMdClose />} onClick={onClose} variant="icon" />}
        </div>
        {loading ? (
          <EASkeletonLoader width="100%" height="200px" />
        ) : (
          <>
            <div className={`eamodal-body${stickyFooter && !hideButtons ? " eamodal-body--with-sticky-footer" : ""}`}>
              {children}
            </div>
            {!hideButtons && (
              <div className={`eamodal-footer${stickyFooter ? " eamodal-footer--sticky" : ""}`}>
                <EAButton variant="outlined" title={cancelButtonLabel || t("anuluj")} onClick={onClose} />
                {!hideAcceptButton && <EAButton title={acceptButtonLabel || t("akceptuj")} onClick={onAccept} disabled={disabledAccept} />}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default EAModal;
